import { useInfiniteQuery } from "@tanstack/react-query";
import { client as apiClient } from "../services/api/client.gen";
import type {
  ActivityEvent,
  ActivityEventSlug,
  ActivityFeedPagination,
} from "../types/activity.types";

interface ListEventsApiResponse {
  data: ActivityEvent[];
  meta?: {
    pagination: ActivityFeedPagination;
  };
  error?: {
    message: string;
    breadcrumb: string;
  } | null;
}

interface FetchEventsOptions {
  pageSize?: number;
  since?: string;
  eventTypes?: ActivityEventSlug[];
  cursor?: string;
}

async function fetchEvents(
  scope: { projectId: string } | { teamId: string } | "user",
  options?: FetchEventsOptions,
): Promise<ListEventsApiResponse> {
  const params = new URLSearchParams();
  if (options?.pageSize) params.set("page_size", String(options.pageSize));
  if (options?.since) params.set("since", options.since);
  if (options?.cursor) params.set("cursor", options.cursor);
  if (options?.eventTypes) {
    for (const et of options.eventTypes) {
      params.append("event_types", et);
    }
  }

  const qs = params.toString();
  const basePath =
    scope === "user"
      ? "/v1/events"
      : "projectId" in scope
        ? `/v1/projects/${scope.projectId}/events`
        : `/v1/teams/${scope.teamId}/events`;
  const url = `${basePath}${qs ? `?${qs}` : ""}`;

  const res = await apiClient.get<ListEventsApiResponse>({ url });

  // The @hey-api client returns { data, request, response } on success
  // but { error, request, response } (no .data) on HTTP errors.
  if (res.error) {
    const errMsg =
      typeof res.error === "object" &&
      res.error !== null &&
      "error" in res.error
        ? String((res.error as Record<string, unknown>).error)
        : "Failed to fetch activity events";
    throw new Error(errMsg);
  }

  return (res.data ?? { data: [] }) as ListEventsApiResponse;
}

export function useActivityFeedInfinite(
  scope: { projectId: string } | { teamId: string } | "user" | undefined,
  options?: {
    pageSize?: number;
    eventTypes?: ActivityEventSlug[];
    since?: string;
    enabled?: boolean;
  },
) {
  const scopeKey = scope
    ? scope === "user"
      ? "user"
      : "projectId" in scope
        ? `project:${scope.projectId}`
        : `team:${scope.teamId}`
    : null;

  return useInfiniteQuery({
    queryKey: ["activity-feed", scopeKey, options?.eventTypes, options?.since],
    queryFn: async ({ pageParam }) => {
      const result = await fetchEvents(scope!, {
        pageSize: options?.pageSize ?? 20,
        eventTypes: options?.eventTypes,
        since: options?.since,
        cursor: pageParam,
      });
      return result;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage?.meta?.pagination?.hasNext
        ? (lastPage.meta.pagination.nextCursor ?? undefined)
        : undefined,
    enabled: !!scope && options?.enabled !== false,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}
